import { ShieldCheck, Lock, FileCheck, Globe } from "lucide-react";
import { useContent } from "@/lib/ContentContext";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { ClosingCTA } from "@/pages/Home";
import { IdentityGraph } from "@/components/mocks/ProductMocks";

const targeting = [
  ["Contextual AI", "Semantic page and content analysis — no cookies required."],
  ["First-party audiences", "Onboard CRM segments into your own portable graph."],
  ["Behavioral & intent", "Consented signals across the open web, fully disclosed."],
  ["Geo & dayparting", "Down to postcode, hour and weather trigger."],
  ["Lookalike modeling", "Outcome-trained expansion on your best converters."],
  ["Sequential messaging", "Story arcs across channels and devices, frequency-safe."],
];

const compliance = [
  { icon: Globe, name: "GDPR", desc: "EU-lawful processing, consent signals honored end-to-end." },
  { icon: Lock, name: "CCPA / CPRA", desc: "US state privacy compliance with documented data flows." },
  { icon: FileCheck, name: "COPPA", desc: "Child-directed inventory excluded at the supply layer." },
  { icon: ShieldCheck, name: "ISO 27001", desc: "Security-certified infrastructure and access controls. (Pending final certification.)" },
];

export default function AudienceData() {
  const { identity } = useContent();
  return (
    <>
      <section className="border-b border-[#D8E2E6]" data-testid="audience-hero">
        <div className="container-riq pt-16 pb-20 lg:pt-24 lg:pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <p className="eyebrow mb-5">Audience & Data</p>
            <h1 className="font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
              <MaskedLine delay={0.1}>Precision without</MaskedLine>
              <MaskedLine delay={0.22}>
                privacy <span className="accent-word">compromise.</span>
              </MaskedLine>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#4A5A63] leading-relaxed max-w-xl">
              Durable addressability built on consent: UID2, your first-party graph, contextual AI fallback and clean-room measurement. No dark patterns, no borrowed identity.
            </p>
          </div>
          <div className="lg:col-span-6">
            <IdentityGraph />
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="targeting-options">
        <div className="container-riq">
          <Reveal>
            <p className="eyebrow mb-3">Targeting</p>
            <h2 className="font-display font-black tracking-tight text-2xl sm:text-3xl lg:text-4xl">
              Aim with <span className="accent-word">evidence.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {targeting.map(([t, d], i) => (
              <Reveal key={t} delay={(i % 3) * 0.08}>
                <div className="border-t border-[#D8E2E6] pt-4" data-testid={`targeting-${i}`}>
                  <span className="font-mono2 text-xs font-bold text-[#0E7A8F]">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display font-bold text-lg tracking-tight mt-1">{t}</h3>
                  <p className="text-sm text-[#4A5A63] mt-1.5 leading-relaxed">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#EDF3F5] border-y border-[#D8E2E6]" data-testid="identity-solutions">
        <div className="container-riq">
          <Reveal>
            <p className="eyebrow mb-3">Identity solutions</p>
            <h2 className="font-display font-black tracking-tight text-2xl sm:text-3xl lg:text-4xl">
              Four ways to stay <span className="accent-word">addressable.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {identity.map((idn, i) => (
              <Reveal key={idn.id} delay={(i % 2) * 0.1}>
                <article className="card-brut-sm card-hover p-6" data-testid={`identity-${idn.id}`}>
                  <span className="font-display font-black text-4xl text-[#0D0D0F]/10">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display font-bold text-xl tracking-tight mt-2">{idn.name}</h3>
                  <p className="text-sm text-[#4A5A63] mt-2 leading-relaxed">{idn.desc}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="privacy-compliance">
        <div className="container-riq">
          <Reveal>
            <p className="eyebrow mb-3">Privacy & compliance</p>
            <h2 className="font-display font-black tracking-tight text-2xl sm:text-3xl lg:text-4xl">
              Compliant by <span className="accent-word">architecture.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {compliance.map((c, i) => (
              <Reveal key={c.name} delay={(i % 4) * 0.08}>
                <div className="border border-[#D8E2E6] bg-white p-5 h-full" data-testid={`compliance-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <c.icon className="w-6 h-6 text-[#0E7A8F]" aria-hidden="true" />
                  <h3 className="font-display font-bold text-lg tracking-tight mt-3">{c.name}</h3>
                  <p className="text-xs text-[#4A5A63] mt-1.5 leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <ClosingCTA />
    </>
  );
}
